// Structured error for a locked PRD conversation (HTTP 409).
//
// Before change 07 the service layer collapsed every non-OK response into a
// bare `throw new Error("...string...")`, discarding the HTTP status and the
// JSON body. That made it impossible for the UI to tell a lock 409 from any
// other failure, or to read the `code` discriminator.
//
// LockError carries the full contract { status, code, reason, error } so the
// hook/UI can branch on `code` (never on the Spanish text) and show `reason`.

export class LockError extends Error {
    /**
     * @param {Object} params
     * @param {number} params.status  HTTP status (409 for locks)
     * @param {string|null} params.code    stable discriminator (locked_*)
     * @param {string|null} params.reason  Spanish user-facing text
     */
    constructor({ status, code, reason, message }) {
        super(message || reason || 'Conversación bloqueada');
        this.name = 'LockError';
        this.status = status;
        this.code = code || null;
        this.reason = reason || null;
        this.isLock = Boolean(code);
    }
}

/**
 * Inspects a non-OK fetch Response and throws the appropriate error.
 *
 * On a 409 carrying a `code`, throws a LockError with the parsed contract.
 * Otherwise throws a plain Error using the backend `error` text when present,
 * falling back to the provided message. This is defensive: an older backend
 * that answers 409 with a bare `{ error }` (no `code`) yields a plain Error,
 * preserving the pre-change behavior.
 *
 * Always throws — never returns.
 *
 * @param {Response} res
 * @param {string} fallbackMessage
 * @returns {Promise<never>}
 */
export async function throwResponseError(res, fallbackMessage) {
    let body = null;
    try {
        body = await res.json();
    } catch (_) {
        // Body was not JSON (or empty). Fall through to the generic error.
    }

    if (res.status === 409 && body && body.code) {
        throw new LockError({
            status: res.status,
            code: body.code,
            reason: body.reason || body.error || null,
        });
    }

    const message = (body && body.error) || fallbackMessage || `HTTP ${res.status}`;
    throw new Error(message);
}
